/**
 * Server-side admin authorization guard for TanStack Start `beforeLoad` hooks.
 *
 * Where {@link requireAuth} only proves a session exists, {@link requireAdmin}
 * also proves the signed-in user holds the `admin` role — the authorization the
 * `/admin` route subtree needs. The decision logic (which redirect, when) lives
 * in `server-auth.ts` (`checkAdmin`, unit-tested); this module supplies the
 * server-only RPC plumbing that loads the user's roles.
 *
 * Importing this module is server-only — it constructs an RPC client and reads
 * the request cookie, which is acceptable inside a `createServerFn` handler but
 * never on the client. The heavy RPC/schema imports are tree-shaken from the
 * client bundle because the handler is extracted at build time by the TanStack
 * Start plugin (same pattern as `orca-ssr.ts`).
 *
 * @since 1.0.0
 */
import { auth } from "@clerk/tanstack-react-start/server";
import { RpcClient } from "@effect/rpc";
import { AuthRpcs } from "@fm/api/rpc";
import { createServerFn } from "@tanstack/react-start";
import { getCookie, getRequestHeaders } from "@tanstack/react-start/server";
import { Effect } from "effect";
import {
  type AdminAuthResult,
  type AdminRolesOutcome,
  checkAdmin,
  classifyAdminRolesError,
  forwardedHostFromHeaders,
  loginRedirectFromHeaders,
  returnToFromRequest,
  returnToMiddleware,
  toLoginRoutePath,
} from "./server-auth.ts";
import { makeServerRpcLayer } from "./server-rpc.ts";

/**
 * Resolve the Clerk bearer token for the current request, or `null` when no
 * authenticated session exists. The `__session` cookie value IS the Clerk
 * session JWT (see `orca-ssr.ts` for the full rationale); reading it directly
 * is < 1 ms and never round-trips to Clerk.
 *
 * @since 1.0.0
 */
const getServerToken = (): string | null => getCookie("__session") ?? null;

/**
 * Load the signed-in user's roles server-side via the `ensureUser` RPC.
 *
 * Returns a tagged {@link AdminRolesOutcome}: `Unauthorized` when there is no
 * session token or the RPC fails with the API's typed `Unauthorized` error;
 * `Unavailable` for any other failure (transport, API down) so `checkAdmin` can
 * surface a retryable error instead of "Access Denied"; `Roles` on success.
 * `ensureUser` (rather than `me`) mirrors `currentUserAtom`, so a brand-new
 * session resolves to the default `["user"]` role instead of 404-ing.
 *
 * `forwardedHost` is the request's genuine inbound brand host (e.g.
 * `"clawinstall.ai"`). It is forwarded as `X-Forwarded-Host` to the internal
 * API so its auth middleware resolves the SAME brand the user is on and
 * verifies the session token against that brand's Clerk `jwtKey`. Without it the
 * internal cluster hop loses the brand, collapses to the API's `DEFAULT_BRAND`,
 * and verifies a clawinstall `pk_live` token against the FlirtMachine key —
 * which fails closed and denies a real clawinstall admin (the INF-562 failure
 * mode, on this SSR admin-role path). See {@link makeServerRpcLayer}.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- server function returns a Promise
const fetchAdminRoles = (forwardedHost: string): Promise<AdminRolesOutcome> => {
  const token = getServerToken();
  if (!token) return Promise.resolve<AdminRolesOutcome>({ _tag: "Unauthorized" });
  const rpcLayer = makeServerRpcLayer(token, forwardedHost);
  return Effect.runPromise(
    RpcClient.make(AuthRpcs).pipe(
      Effect.flatMap((client) => client.ensureUser(void 0)),
      Effect.map((person): AdminRolesOutcome => ({ _tag: "Roles", roles: person.roles })),
      Effect.provide(rpcLayer),
      Effect.scoped,
      Effect.catchAll((error) => Effect.succeed(classifyAdminRolesError(error))),
    ),
  );
};

/**
 * Server function that requires an authenticated user **with the `admin` role**.
 *
 * Use this in the `beforeLoad` of admin route subtrees. Unauthenticated
 * visitors (and sessions the API rejects as `Unauthorized`) are sent to the
 * brand's **admin door** (resolved from the request host, like
 * {@link requireAuth}) carrying a `redirect_url` return-to; authenticated
 * non-admins are bounced to `/access-denied`; a role lookup that is merely
 * unavailable throws a retryable error rather than denying access.
 *
 * @example
 * ```typescript
 * export const Route = createFileRoute('/clawinstall/admin')({
 *   beforeLoad: async () => ({ auth: await requireAdmin() }),
 *   component: AdminLayout,
 * })
 * ```
 *
 * @since 1.0.0
 */
export const requireAdmin = createServerFn({ method: "GET" })
  .middleware([returnToMiddleware])
  .handler(
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await for redirects
    (): Promise<AdminAuthResult> => {
      // Read the inbound request headers ONCE: the login redirect, the return-to
      // and the brand host forwarded on the role-lookup RPC must agree on the
      // brand the visitor is on, so all derive from the same header set.
      const headers = getRequestHeaders();
      const forwardedHost = forwardedHostFromHeaders(headers);
      return checkAdmin(
        toLoginRoutePath(loginRedirectFromHeaders(headers)),
        returnToFromRequest(headers),
        () => fetchAdminRoles(forwardedHost),
        auth,
      );
    },
  );
